import React, { useState, useEffect, useCallback, useContext } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight,
  faCheckCircle,
  faHourglassHalf,
  faFire
} from '@fortawesome/free-solid-svg-icons';
import { Card, Spinner, Button } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import AgentFilterSelect from './AgentFilterSelect';

registerLocale('es', es);

echarts.use([
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  MarkLineComponent,
  MarkPointComponent,
  CanvasRenderer
]);

const formatLocalDateStr = dt => {
  if (!dt) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDuration = minutes => {
  if (!minutes || minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const ResolutionTimeChart = ({
  selectedAgentId = '',
  onSelectAgent,
  agents = [],
  currentUserId,
  currentUserName,
  isLoadingAgents = false
}) => {
  const { token, idUsuario, nombreCompleto } = useToken();
  const { config: { isDark } = {} } = useContext(AppContext);

  const effectiveUserId = currentUserId || idUsuario;
  const effectiveUserName = currentUserName || nombreCompleto;

  const today = new Date();
  const todayStr = formatLocalDateStr(today);
  const [selectedDateObj, setSelectedDateObj] = useState(today);
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const selectedDate = formatLocalDateStr(selectedDateObj);

  const fetchChartData = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      const hasAgent = selectedAgentId && selectedAgentId !== 'todos' && selectedAgentId !== 'all';
      if (hasAgent) {
        params.set('idUsuarioAsignado', selectedAgentId);
      }
      if (selectedDate) {
        params.set('dFecha', selectedDate);
      }

      const resp = await fetch(`/api/SupportEngine/GetDashboardResolutionTimeChart?${params.toString()}`, {
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        }
      });

      if (resp.ok) {
        const json = await resp.json();
        const data = json.data || json || [];
        setChartData(Array.isArray(data) ? data : []);
      }
    } catch {
      // Silencioso
    } finally {
      setIsLoading(false);
    }
  }, [token, selectedAgentId, selectedDate]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // Navegación de fechas
  const handlePrevDay = () => {
    const prev = new Date(selectedDateObj);
    prev.setDate(prev.getDate() - 1);
    setSelectedDateObj(prev);
  };

  const handleNextDay = () => {
    const next = new Date(selectedDateObj);
    next.setDate(next.getDate() + 1);
    setSelectedDateObj(next);
  };

  const handleGoToday = () => {
    setSelectedDateObj(new Date());
  };

  const isToday = selectedDate === todayStr;
  const currentHour = today.getHours();

  const categories = [];
  const resolucionMap = {};
  const resueltosHoraMap = {};

  let promedioResolucionDia = 0;
  let totalResueltosDia = 0;

  chartData.forEach(item => {
    const h = item.iHora;
    if (h !== undefined && h !== null && (!isToday || h <= currentHour || item.iResueltosHora > 0)) {
      const label = item.vLabel || `${String(h).padStart(2, '0')}:00`;
      categories.push(label);
      resolucionMap[label] = item.iTiempoResolucionPromedioMinutos ?? 0;
      resueltosHoraMap[label] = item.iResueltosHora ?? 0;
    }
    if (item.iTiempoResolucionPromedioDia) {
      promedioResolucionDia = item.iTiempoResolucionPromedioDia;
    }
    if (item.iTotalResueltosDia) {
      totalResueltosDia = item.iTotalResueltosDia;
    }
  });

  const seriesResolucion = categories.map(cat => resolucionMap[cat] || 0);
  const seriesPromedioDia = categories.map(cat => (resueltosHoraMap[cat] > 0 ? promedioResolucionDia : 0));

  const maxResolucion = Math.max(...seriesResolucion, 0);

  // Configuración de ECharts
  const getOption = () => {
    const textColor = isDark ? '#94a3b8' : '#64748b';
    const splitLineColor = isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)';
    const axisLineColor = isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)';

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        borderColor: isDark ? '#334155' : '#e2e8f0',
        borderWidth: 1,
        textStyle: { color: isDark ? '#f8fafc' : '#1e293b', fontSize: 12 },
        formatter: params => {
          if (!params || params.length === 0) return '';
          const hourLabel = params[0].name;
          const ticketsHora = resueltosHoraMap[hourLabel] || 0;

          if (ticketsHora === 0) {
            return `<div style="font-weight: 600; padding: 2px 4px; color: ${textColor};">Hora ${hourLabel}: Sin tickets cerrados</div>`;
          }

          let html = `<div style="font-weight: 700; margin-bottom: 6px; padding-bottom: 4px; border-bottom: 1px solid ${isDark ? '#334155' : '#e2e8f0'}">
            Hora: ${hourLabel} <span style="font-size: 11px; opacity: 0.85; font-weight: normal;">(${ticketsHora} tickets cerrados)</span>
          </div>`;

          params.forEach(item => {
            const min = item.value || 0;
            if (min > 0) {
              const dur = formatDuration(min);
              html += `
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 3px;">
                  <span>${item.marker} ${item.seriesName}:</span>
                  <span style="font-weight: 700;">${dur} <span style="font-size: 10px; opacity: 0.75;">(${min} min)</span></span>
                </div>
              `;
            }
          });

          return html;
        }
      },
      legend: {
        data: ['Tiempo Resolución (Hora)', 'Promedio del Día'],
        top: 0,
        right: 10,
        textStyle: {
          color: textColor,
          fontSize: 12,
          fontWeight: 600
        },
        itemWidth: 14,
        itemHeight: 10
      },
      grid: {
        top: 36,
        left: 48,
        right: 20,
        bottom: 24,
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        axisLabel: {
          color: textColor,
          fontSize: 11,
          fontFamily: 'Inter, system-ui, sans-serif'
        }
      },
      yAxis: {
        type: 'value',
        name: 'Minutos laborales',
        nameTextStyle: {
          color: textColor,
          fontSize: 11,
          padding: [0, 0, 4, 0]
        },
        splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: val => formatDuration(val)
        }
      },
      series: [
        {
          name: 'Tiempo Resolución (Hora)',
          type: 'bar',
          data: seriesResolucion,
          barMaxWidth: 26,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#38bdf8' },
              { offset: 1, color: '#0284c7' }
            ])
          },
          label: {
            show: true,
            position: 'top',
            formatter: params => (params.value > 0 ? formatDuration(params.value) : ''),
            fontSize: 10,
            fontWeight: 'bold',
            color: isDark ? '#38bdf8' : '#0284c7'
          },
          emphasis: {
            itemStyle: {
              color: '#0ea5e9'
            }
          }
        },
        {
          name: 'Promedio del Día',
          type: 'bar',
          data: seriesPromedioDia,
          barMaxWidth: 26,
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#34d399' },
              { offset: 1, color: '#10b981' }
            ])
          },
          label: {
            show: true,
            position: 'top',
            formatter: params => (params.value > 0 ? formatDuration(params.value) : ''),
            fontSize: 10,
            fontWeight: 'bold',
            color: isDark ? '#34d399' : '#059669'
          },
          emphasis: {
            itemStyle: {
              color: '#059669'
            }
          }
        }
      ]
    };
  };

  return (
    <Card className="mb-3">
      {/* Header con Título, Badges y Filtros */}
      <Card.Header className="bg-light py-2 px-3 px-md-4">
        {/* Fila Principal: Título a la izquierda y Controles a la derecha */}
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2 pb-2">
          <div>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faClock} style={{ color: '#0284c7', fontSize: '1.15rem' }} />
              <h5 className="mb-0 fw-bold text-900" style={{ fontSize: '1.05rem' }}>
                Tiempos de Resolución y Promedio Diario
              </h5>
            </div>
            <p className="text-500 mb-0 mt-1" style={{ fontSize: '0.80rem' }}>
              Tiempo de resolución por hora (azul) vs promedio general del día (verde).
            </p>
          </div>

          {/* Controles en una sola fila a la derecha */}
          <div className="d-flex align-items-center gap-2 flex-wrap flex-sm-nowrap">
            {onSelectAgent && (
              <div style={{ minWidth: '180px' }}>
                <AgentFilterSelect
                  selectedAgentId={selectedAgentId}
                  onSelectAgent={onSelectAgent}
                  agents={agents}
                  currentUserId={effectiveUserId}
                  currentUserName={effectiveUserName}
                  isLoadingAgents={isLoadingAgents}
                />
              </div>
            )}

            {/* Navegador de Fecha con DatePicker */}
            <div
              className="d-flex align-items-center rounded-2 px-1 border"
              style={{
                height: '36px',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#d8e2ef'
              }}
            >
              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary text-decoration-none"
                onClick={handlePrevDay}
                title="Día anterior"
              >
                <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: '0.75rem' }} />
              </Button>

              <div className="d-flex align-items-center px-1">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="text-primary me-1"
                  style={{ fontSize: '0.85rem' }}
                />
                <DatePicker
                  selected={selectedDateObj}
                  onChange={date => date && setSelectedDateObj(date)}
                  locale="es"
                  dateFormat="d 'de' MMMM, yyyy"
                  maxDate={today}
                  className="form-control form-control-sm border-0 bg-transparent text-center px-1"
                  style={{
                    minWidth: '150px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: isDark ? '#f8fafc' : '#1e293b'
                  }}
                />
              </div>

              <Button
                variant="link"
                size="sm"
                className="p-1 text-secondary text-decoration-none"
                onClick={handleNextDay}
                disabled={isToday}
                title="Día siguiente"
              >
                <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: '0.75rem' }} />
              </Button>
            </div>

            {!isToday && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="px-2"
                style={{ height: '36px', fontSize: '0.80rem', fontWeight: 600 }}
                onClick={handleGoToday}
              >
                Hoy
              </Button>
            )}
          </div>
        </div>

        {/* Fila Inferior: Badges de Resumen alineados horizontalmente */}
        <div
          className="d-flex align-items-center gap-2 flex-wrap pt-2 border-top"
          style={{ borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)' }}
        >
          <div
            className="px-2 py-1 rounded-2 d-flex align-items-center gap-1 border"
            style={{
              backgroundColor: isDark ? 'rgba(16, 185, 129, 0.12)' : '#ecfdf5',
              borderColor: isDark ? 'rgba(16, 185, 129, 0.25)' : '#a7f3d0',
              color: isDark ? '#34d399' : '#059669',
              fontSize: '0.78rem',
              fontWeight: 600
            }}
            title="Promedio general del día de resolución"
          >
            <FontAwesomeIcon icon={faHourglassHalf} />
            <span>Promedio del Día: <strong>{formatDuration(promedioResolucionDia)}</strong></span>
          </div>

          {maxResolucion > 0 && (
            <div
              className="px-2 py-1 rounded-2 d-flex align-items-center gap-1 border"
              style={{
                backgroundColor: isDark ? 'rgba(2, 132, 199, 0.12)' : '#f0f9ff',
                borderColor: isDark ? 'rgba(2, 132, 199, 0.25)' : '#bae6fd',
                color: isDark ? '#38bdf8' : '#0284c7',
                fontSize: '0.78rem',
                fontWeight: 600
              }}
              title="Pico más alto de resolución en una hora"
            >
              <FontAwesomeIcon icon={faFire} />
              <span>Pico por Hora: <strong>{formatDuration(maxResolucion)}</strong></span>
            </div>
          )}

          <div
            className="px-2 py-1 rounded-2 d-flex align-items-center gap-1 border"
            style={{
              backgroundColor: isDark ? 'rgba(99, 102, 241, 0.12)' : '#f5f3ff',
              borderColor: isDark ? 'rgba(99, 102, 241, 0.25)' : '#ddd6fe',
              color: isDark ? '#818cf8' : '#6366f1',
              fontSize: '0.78rem',
              fontWeight: 600
            }}
            title="Total de tickets cerrados en la jornada"
          >
            <FontAwesomeIcon icon={faCheckCircle} />
            <span>Resueltos: <strong>{totalResueltosDia}</strong></span>
          </div>
        </div>
      </Card.Header>

      {/* Cuerpo de la Card con el Gráfico */}
      <Card.Body className="px-3 px-md-4 pt-3 pb-3">
        <div style={{ position: 'relative', width: '100%', minHeight: '320px' }}>
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.65)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px'
              }}
            >
              <Spinner animation="border" variant="info" size="sm" />
            </div>
          )}

          {categories.length === 0 && !isLoading ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center text-muted"
              style={{ height: '320px' }}
            >
              <FontAwesomeIcon icon={faClock} style={{ fontSize: '2rem', opacity: 0.35, marginBottom: '8px' }} />
              <p className="small mb-0">No hay tickets cerrados en este día para el filtro seleccionado.</p>
            </div>
          ) : (
            <ReactEChartsCore
              echarts={echarts}
              option={getOption()}
              notMerge={true}
              lazyUpdate={true}
              style={{ height: '320px', width: '100%' }}
            />
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ResolutionTimeChart;
